import { createTheme, type ThemeOptions, type Theme } from '@mui/material/styles';
import { typography, palette, radii, shadows, transitions, zIndex } from './tokens';

// Extend MUI theme to include custom properties
declare module '@mui/material/styles' {
  interface Palette {
    priority: {
      p1: { main: string; bg: string };
      p2: { main: string; bg: string };
      p3: { main: string; bg: string };
      p4: { main: string; bg: string };
    };
  }
  interface PaletteOptions {
    priority?: {
      p1: { main: string; bg: string };
      p2: { main: string; bg: string };
      p3: { main: string; bg: string };
      p4: { main: string; bg: string };
    };
  }
}

// Extend Button variants
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    ghost: true;
  }
  interface ButtonPropsColorOverrides {
    ghost: true;
  }
}

const baseThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: typography.fontFamily.sans,
    fontSize: 14,
    fontWeightLight: typography.fontWeight.normal,
    fontWeightRegular: typography.fontWeight.normal,
    fontWeightMedium: typography.fontWeight.medium,
    fontWeightBold: typography.fontWeight.bold,
    h1: {
      fontSize: typography.fontSize['3xl'],
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.tight,
    },
    h2: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.tight,
    },
    h3: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.snug,
    },
    h4: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.snug,
    },
    h5: {
      fontSize: typography.fontSize.md,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
    },
    h6: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      lineHeight: typography.lineHeight.normal,
    },
    body1: {
      fontSize: typography.fontSize.md,
      lineHeight: typography.lineHeight.normal,
    },
    body2: {
      fontSize: typography.fontSize.base,
      lineHeight: typography.lineHeight.normal,
    },
    caption: {
      fontSize: typography.fontSize.sm,
      lineHeight: typography.lineHeight.normal,
    },
    overline: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.semibold,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    button: {
      fontSize: typography.fontSize.md,
      fontWeight: typography.fontWeight.medium,
      textTransform: 'uppercase',
      letterSpacing: '0.02857em',
    },
  },
  shape: {
    borderRadius: radii.md,
  },
  spacing: 8, // Base spacing unit (8px)
  zIndex: {
    mobileStepper: zIndex.base,
    fab: zIndex.fixed,
    speedDial: zIndex.fixed,
    appBar: zIndex.sticky,
    drawer: zIndex.drawer,
    modal: zIndex.modal,
    snackbar: zIndex.toast,
    tooltip: zIndex.tooltip,
  },
  transitions: {
    duration: {
      shortest: transitions.duration.instant,
      shorter: transitions.duration.fast,
      short: transitions.duration.normal,
      standard: transitions.duration.moderate,
      complex: transitions.duration.slow,
      enteringScreen: transitions.duration.moderate,
      leavingScreen: transitions.duration.normal,
    },
    easing: {
      easeInOut: transitions.easing.easeInOut,
      easeOut: transitions.easing.easeOut,
      easeIn: transitions.easing.easeIn,
      sharp: transitions.easing.easeInOut,
    },
  },
};

const getComponentOverrides = (mode: 'light' | 'dark'): ThemeOptions['components'] => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: typography.fontFamily.sans,
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: false,
      disableRipple: false,
    },
    styleOverrides: {
      root: {
        borderRadius: radii.md,
        textTransform: 'uppercase',
        fontWeight: typography.fontWeight.medium,
        transition: `background-color ${transitions.duration.fast}ms ${transitions.easing.easeOut},
                     border-color ${transitions.duration.fast}ms ${transitions.easing.easeOut},
                     box-shadow ${transitions.duration.fast}ms ${transitions.easing.easeOut}`,
      },
      sizeSmall: {
        padding: '4px 10px',
        fontSize: typography.fontSize.base,
      },
      sizeMedium: {
        padding: '6px 16px',
        fontSize: typography.fontSize.md,
      },
      sizeLarge: {
        padding: '8px 22px',
        fontSize: '0.9375rem',
      },
      contained: {
        boxShadow: shadows.sm,
        '&:hover': {
          boxShadow: shadows.md,
        },
      },
      outlined: {
        borderWidth: 1,
        '&:hover': {
          borderWidth: 1,
        },
      },
    },
    variants: [
      {
        props: { variant: 'ghost' },
        style: {
          backgroundColor: 'transparent',
          border: `1px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'}`,
          color: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
          '&:hover': {
            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
            borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
          },
        },
      },
    ],
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: radii.full,
        transition: `background-color ${transitions.duration.fast}ms ${transitions.easing.easeOut}`,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'small',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: radii.md,
          transition: `border-color ${transitions.duration.fast}ms ${transitions.easing.easeOut}`,
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: radii.md,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
        },
      },
      notchedOutline: {
        borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
        transition: `border-color ${transitions.duration.fast}ms ${transitions.easing.easeOut}`,
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      variant: 'outlined',
      size: 'small',
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: radii['2xl'],
        fontWeight: typography.fontWeight.medium,
        fontSize: typography.fontSize.xs,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
      sizeSmall: {
        height: 24,
      },
      sizeMedium: {
        height: 28,
      },
    },
  },
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: radii.md,
        border: `1px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
        boxShadow: shadows.card,
        transition: `box-shadow ${transitions.duration.fast}ms ${transitions.easing.easeOut},
                     border-color ${transitions.duration.fast}ms ${transitions.easing.easeOut}`,
        '&:hover': {
          borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
          boxShadow: shadows.cardHover,
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: radii.md,
      },
      elevation1: {
        boxShadow: shadows.sm,
      },
      elevation2: {
        boxShadow: shadows.md,
      },
      elevation3: {
        boxShadow: shadows.lg,
      },
      elevation4: {
        boxShadow: shadows.xl,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: radii.xl,
        boxShadow: shadows.modal,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: radii.lg,
        boxShadow: shadows.dropdown,
        marginTop: 4,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: typography.fontSize.md,
        padding: '8px 16px',
        transition: `background-color ${transitions.duration.fast}ms ${transitions.easing.easeOut}`,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: typography.fontSize.sm,
        borderRadius: radii.md,
        padding: '6px 12px',
      },
    },
  },
  MuiSnackbar: {
    styleOverrides: {
      root: {
        '& .MuiSnackbarContent-root': {
          borderRadius: radii.md,
          boxShadow: shadows.lg,
        },
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: radii.md,
      },
      standardSuccess: {
        backgroundColor: mode === 'light' ? '#e8f5e9' : 'rgba(102, 187, 106, 0.12)',
      },
      standardError: {
        backgroundColor: mode === 'light' ? '#ffebee' : 'rgba(244, 67, 54, 0.12)',
      },
      standardWarning: {
        backgroundColor: mode === 'light' ? '#fff3e0' : 'rgba(255, 167, 38, 0.12)',
      },
      standardInfo: {
        backgroundColor: mode === 'light' ? '#e3f2fd' : 'rgba(144, 202, 249, 0.12)',
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: {
        borderRadius: radii.md,
      },
      rectangular: {
        borderRadius: radii.md,
      },
      circular: {
        borderRadius: radii.full,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 3,
        borderRadius: '3px 3px 0 0',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: typography.fontWeight.medium,
        fontSize: typography.fontSize.md,
        minWidth: 'auto',
        padding: '12px 16px',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontSize: typography.fontSize.md,
        padding: '12px 16px',
      },
      head: {
        fontWeight: typography.fontWeight.semibold,
        fontSize: typography.fontSize.xs,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
      },
    },
  },
});

export const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  const paletteColors = mode === 'light' ? palette.light : palette.dark;

  return createTheme({
    ...baseThemeOptions,
    palette: {
      mode,
      primary: paletteColors.primary,
      secondary: paletteColors.primary, // Use primary as secondary for consistency
      success: paletteColors.success,
      warning: paletteColors.warning,
      error: paletteColors.error,
      info: paletteColors.info,
      text: paletteColors.text,
      background: paletteColors.background,
      divider: paletteColors.divider,
      action: paletteColors.action,
      priority: paletteColors.priority,
    },
    components: getComponentOverrides(mode),
  });
};

// Default light theme
export const lightTheme = createAppTheme('light');

// Dark theme
export const darkTheme = createAppTheme('dark');

// Re-export tokens for direct access if needed
export * from './tokens';
